import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  Film, Loader2, X, Download, AlertTriangle, Sparkles,
  Music, Mic, Upload, GripVertical, Check, Trash2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "storylo_admin_token";

const PROMO_LANGS = [
  { code: "en", label: "English" }, { code: "de", label: "German" },
  { code: "fr", label: "French" },  { code: "es", label: "Spanish" },
  { code: "it", label: "Italian" }, { code: "el", label: "Greek" },
  { code: "tr", label: "Turkish" }, { code: "ar", label: "Arabic" },
  { code: "nl", label: "Dutch" },   { code: "ru", label: "Russian" },
];

/* ──────────────────────────────────────────────────────────────────────────
 * PromoReelPanel
 *
 * Builds a branded promo MP4 from the creator's stories. Three sections:
 *  1. Story picker — load published library, tick the stories to include,
 *     drag the picked rows to reorder. Empty selection auto-picks the
 *     top-N by view count (legacy behaviour).
 *  2. Audio — tabs: None / Upload music / Type narration. Music uploads
 *     to /api/admin/promo-reel/audio first, then the audio_id is passed
 *     to the build. Narration is TTS-rendered server-side from text +
 *     chosen language + voice.
 *  3. Clip-length slider (per-story seconds).
 *
 * Build → poll job → render previewable <video> + Download MP4.
 * ────────────────────────────────────────────────────────────────────── */
export default function PromoReelPanel() {
  const [open, setOpen] = useState(false);
  const [clipSeconds, setClipSeconds] = useState(4);

  // Story picker state
  const [stories, setStories] = useState([]);          // all eligible
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [picked, setPicked] = useState([]);            // ordered list of ids
  const [dragId, setDragId] = useState(null);

  // Audio state
  const [audioMode, setAudioMode] = useState("none");  // none | music | narration
  const [audioFile, setAudioFile] = useState(null);    // File obj (pre-upload)
  const [audioUploaded, setAudioUploaded] = useState(null); // { audio_id, filename, size }
  const [audioUploading, setAudioUploading] = useState(false);
  const [narrationText, setNarrationText] = useState("");
  const [narrationLang, setNarrationLang] = useState("en");
  const [voicesByLang, setVoicesByLang] = useState({});
  const [narrationVoice, setNarrationVoice] = useState("");

  // Narration + background-music combo. When `bgMusicEnabled` is true
  // inside the Narration tab, the creator can upload a music track that
  // plays underneath the spoken voice-over at `bgMusicVolume` (0–1).
  const [bgMusicEnabled, setBgMusicEnabled] = useState(false);
  const [bgMusicFile, setBgMusicFile] = useState(null);
  const [bgMusicUploaded, setBgMusicUploaded] = useState(null);
  const [bgMusicUploading, setBgMusicUploading] = useState(false);
  const [bgMusicVolume, setBgMusicVolume] = useState(0.25);

  // Job state
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const tokenRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    tokenRef.current = (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_KEY)) || "";
  }, []);

  // Voice list (cached on first open). Public endpoint — no token needed.
  useEffect(() => {
    if (!open || Object.keys(voicesByLang).length > 0) return;
    axios.get(`${API}/voices`).then((r) => {
      setVoicesByLang(r.data || {});
      // Pick a sensible default voice for the current narration lang.
      const first = (r.data?.[narrationLang] || [])[0];
      if (first) setNarrationVoice(first.id);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When narration language changes, swap the default voice.
  useEffect(() => {
    const list = voicesByLang[narrationLang] || [];
    if (list.length > 0 && !list.find((v) => v.id === narrationVoice)) {
      setNarrationVoice(list[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrationLang, voicesByLang]);

  const ensureToken = () => {
    if (tokenRef.current) return tokenRef.current;
    const t = window.prompt("Admin token (Studio):", "storylo-admin");
    if (!t) return "";
    tokenRef.current = t;
    try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* blocked */ }
    return t;
  };

  // Load eligible stories the first time the modal opens.
  useEffect(() => {
    if (!open || stories.length > 0) return;
    const token = ensureToken();
    if (!token) return;
    setStoriesLoading(true);
    axios.get(`${API}/admin/stories-for-promo`, {
      headers: { "X-Admin-Token": token },
    }).then((r) => {
      setStories(r.data?.stories || []);
    }).catch((e) => {
      if (e?.response?.status === 403) {
        try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* blocked */ }
        tokenRef.current = null;
      }
      setErr(e?.response?.data?.detail || "Couldn't load stories.");
    }).finally(() => setStoriesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll the job until it finishes.
  useEffect(() => {
    if (!running || !job?.id) return undefined;
    const token = tokenRef.current;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/promo-reel/${job.id}`, {
          headers: { "X-Admin-Token": token },
        });
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "failed") {
          setRunning(false);
          clearInterval(pollRef.current);
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          setRunning(false);
          clearInterval(pollRef.current);
          setErr("Job disappeared from the server. Try again.");
        }
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, job?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !running) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, running]);

  const onClose = () => {
    if (running) return;
    setOpen(false);
    setErr("");
    setJob(null);
  };

  const togglePick = (id) => {
    setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };

  // Drag-reorder picked stories (HTML5 drag-and-drop).
  const onDragStart = (id) => () => setDragId(id);
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === id) return;
    setPicked((p) => {
      const from = p.indexOf(dragId);
      const to = p.indexOf(id);
      if (from === -1 || to === -1) return p;
      const next = [...p];
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      return next;
    });
  };
  const onDragEnd = () => setDragId(null);

  // Audio file picker → upload to get an audio_id.
  const onAudioFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAudioFile(f);
    setAudioUploaded(null);
    setErr("");
    const token = ensureToken();
    if (!token) return;
    setAudioUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/admin/promo-reel/audio`, fd, {
        headers: { "X-Admin-Token": token },
      });
      setAudioUploaded(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't upload audio.");
      setAudioFile(null);
    } finally {
      setAudioUploading(false);
    }
  };

  // Background-music file picker — used only when audioMode === 'narration'
  // and the creator wants quiet music under their voice-over. Hits the
  // same upload endpoint as the main music tab; we keep the IDs separate
  // so the build payload can reference both at once.
  const onBgMusicFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBgMusicFile(f);
    setBgMusicUploaded(null);
    setErr("");
    const token = ensureToken();
    if (!token) return;
    setBgMusicUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/admin/promo-reel/audio`, fd, {
        headers: { "X-Admin-Token": token },
      });
      setBgMusicUploaded(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't upload background music.");
      setBgMusicFile(null);
    } finally {
      setBgMusicUploading(false);
    }
  };

  const onBuild = async () => {
    setErr("");
    setJob(null);

    // Validation
    if (picked.length > 0 && picked.length < 2) {
      setErr("Pick at least 2 stories — or clear the picker to auto-select your top stories.");
      return;
    }
    if (audioMode === "music" && !audioUploaded?.audio_id) {
      setErr("Upload a music file first, or switch to None / Narration.");
      return;
    }
    if (audioMode === "narration" && !narrationText.trim()) {
      setErr("Type the narration line you want voiced, or switch to None / Music.");
      return;
    }
    if (audioMode === "narration" && bgMusicEnabled && !bgMusicUploaded?.audio_id) {
      setErr("Upload a background music file, or turn off the music toggle.");
      return;
    }

    setRunning(true);
    try {
      const token = ensureToken();
      if (!token) { setRunning(false); return; }
      const body = {
        count: picked.length > 0 ? picked.length : 5,
        clip_seconds: clipSeconds,
        story_ids: picked,
      };
      if (audioMode === "music") {
        body.audio = { kind: "music", audio_id: audioUploaded.audio_id };
      } else if (audioMode === "narration") {
        body.audio = {
          kind: "narration",
          text: narrationText.trim(),
          lang: narrationLang,
          voice: narrationVoice,
        };
        if (bgMusicEnabled && bgMusicUploaded?.audio_id) {
          body.audio.bg_music_audio_id = bgMusicUploaded.audio_id;
          body.audio.bg_music_volume = bgMusicVolume;
        }
      }
      const r = await axios.post(`${API}/admin/promo-reel`, body,
        { headers: { "X-Admin-Token": token } });
      setJob({ id: r.data.job_id, status: "queued", progress: 0, message: "Queued" });
    } catch (e) {
      setRunning(false);
      if (e?.response?.status === 403) {
        try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* blocked */ }
        tokenRef.current = null;
        setErr("Admin token rejected. Click Build again to re-enter.");
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Couldn't start the build.");
      }
    }
  };

  const pickedDocs = useMemo(() => {
    const byId = Object.fromEntries(stories.map((s) => [s.id, s]));
    return picked.map((id) => byId[id]).filter(Boolean);
  }, [picked, stories]);
  const unpickedDocs = useMemo(
    () => stories.filter((s) => !picked.includes(s.id)),
    [picked, stories],
  );

  const effectiveCount = picked.length > 0 ? picked.length : 5;
  const estimatedSeconds = 6 + effectiveCount * Math.max(2, clipSeconds);
  const totalLength = 2 + effectiveCount * clipSeconds - (effectiveCount + 1) * 0.5 + 2;

  const narrationVoices = voicesByLang[narrationLang] || [];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-white/15 hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="promo-reel-open"
        title="Build a shareable promo video from your top stories"
      >
        <Film className="w-3.5 h-3.5" /> Promo reel
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
          onClick={onClose}
          data-testid="promo-reel-modal"
        >
          <div
            className="panel max-w-3xl w-full mx-auto my-12 md:my-6 p-6 md:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Build a promo reel"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Storylo promo
                </div>
                <div className="font-display text-xl leading-tight">
                  Build a shareable promo from your stories
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1.5 leading-relaxed">
                  Tick the stories you want, drag to reorder, optionally add music or a spoken intro — Storylo stitches it into one branded MP4 you can drop into Facebook, Instagram, YouTube, or your landing page.
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[color:var(--muted)] hover:text-white"
                disabled={running}
                data-testid="promo-reel-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!job?.download_url && (
              <>
                {/* ── Story picker ────────────────────────────────── */}
                <section className="mb-5" data-testid="promo-reel-story-picker">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--accent)]">
                      Stories to include
                    </div>
                    <div className="text-[10px] text-[color:var(--muted)]">
                      {picked.length > 0
                        ? `${picked.length} picked · drag to reorder`
                        : `auto-pick top 5 (leave empty)`}
                    </div>
                  </div>

                  {storiesLoading && (
                    <div className="flex items-center gap-2 text-xs text-[color:var(--muted)] py-3">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading stories…
                    </div>
                  )}

                  {!storiesLoading && stories.length === 0 && (
                    <div className="text-xs text-[color:var(--muted)] py-3">
                      Publish at least one story first.
                    </div>
                  )}

                  {pickedDocs.length > 0 && (
                    <ul
                      className="space-y-1 mb-2 p-2 rounded-lg bg-[color:var(--accent)]/5 border border-[color:var(--accent)]/20"
                      data-testid="promo-reel-picked-list"
                    >
                      {pickedDocs.map((s, idx) => (
                        <li
                          key={s.id}
                          draggable
                          onDragStart={onDragStart(s.id)}
                          onDragOver={onDragOver(s.id)}
                          onDragEnd={onDragEnd}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md bg-black/30 hover:bg-black/40 cursor-grab active:cursor-grabbing transition-colors ${dragId === s.id ? "opacity-50" : ""}`}
                          data-testid={`promo-reel-picked-${s.id}`}
                        >
                          <GripVertical className="w-3.5 h-3.5 text-[color:var(--muted)] flex-shrink-0" />
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--accent)] w-5 flex-shrink-0">
                            {idx + 1}
                          </span>
                          {s.thumbnail_url && (
                            <img
                              src={`${BACKEND_URL}${s.thumbnail_url}`}
                              alt=""
                              className="w-10 h-6 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <span className="text-sm truncate flex-1">{s.title}</span>
                          <button
                            onClick={() => togglePick(s.id)}
                            className="text-[color:var(--muted)] hover:text-red-300 flex-shrink-0"
                            title="Remove"
                            data-testid={`promo-reel-remove-${s.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {unpickedDocs.length > 0 && (
                    <details className="text-xs" open={picked.length === 0}>
                      <summary className="cursor-pointer text-[color:var(--muted)] hover:text-white py-1">
                        {picked.length > 0 ? `Add more (${unpickedDocs.length} left)` : `Pick from your library (${unpickedDocs.length})`}
                      </summary>
                      <ul className="space-y-1 mt-2 max-h-56 overflow-y-auto pr-1">
                        {unpickedDocs.map((s) => (
                          <li
                            key={s.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 cursor-pointer transition-colors"
                            onClick={() => togglePick(s.id)}
                            data-testid={`promo-reel-available-${s.id}`}
                          >
                            <div className="w-4 h-4 rounded border border-white/20 flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 opacity-0" />
                            </div>
                            {s.thumbnail_url && (
                              <img
                                src={`${BACKEND_URL}${s.thumbnail_url}`}
                                alt=""
                                className="w-10 h-6 object-cover rounded flex-shrink-0"
                              />
                            )}
                            <span className="text-sm truncate flex-1">{s.title}</span>
                            <span className="text-[10px] text-[color:var(--muted)] flex-shrink-0">
                              {s.view_count || 0} views
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </section>

                {/* ── Audio picker ────────────────────────────────── */}
                <section className="mb-5" data-testid="promo-reel-audio-picker">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--accent)] mb-2">
                    Soundtrack
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      { id: "none", label: "Silent", icon: X },
                      { id: "music", label: "Music", icon: Music },
                      { id: "narration", label: "Narration", icon: Mic },
                    ].map((opt) => {
                      const Icon = opt.icon;
                      const active = audioMode === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setAudioMode(opt.id)}
                          className={`p-3 rounded-lg border text-xs inline-flex items-center justify-center gap-1.5 transition-colors ${
                            active
                              ? "bg-[color:var(--accent)]/10 border-[color:var(--accent)] text-[color:var(--accent)]"
                              : "border-white/10 hover:border-white/30 text-[color:var(--muted)]"
                          }`}
                          data-testid={`promo-reel-audio-mode-${opt.id}`}
                        >
                          <Icon className="w-3.5 h-3.5" /> {opt.label}
                        </button>
                      );
                    })}
                  </div>

                  {audioMode === "music" && (
                    <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02]">
                      <label className="flex items-center gap-2 text-xs cursor-pointer hover:text-[color:var(--accent)]">
                        <Upload className="w-3.5 h-3.5" />
                        <span>{audioFile ? `Replace: ${audioFile.name}` : "Choose audio file (mp3, wav, m4a, ≤ 20 MB)"}</span>
                        <input
                          type="file"
                          accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*"
                          className="hidden"
                          onChange={onAudioFileChosen}
                          data-testid="promo-reel-music-file"
                        />
                      </label>
                      {audioUploading && (
                        <div className="text-[10px] text-[color:var(--muted)] mt-2 inline-flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                        </div>
                      )}
                      {audioUploaded && !audioUploading && (
                        <div className="text-[10px] text-emerald-300 mt-2 inline-flex items-center gap-1.5">
                          <Check className="w-3 h-3" /> {(audioUploaded.size / 1024 / 1024).toFixed(2)} MB uploaded — will play under your stories
                        </div>
                      )}

                      {/* Free-music recommendations — every source below
                          is safe for commercial use (CC0, Mixkit licence,
                          or CC-BY with attribution noted). Saves the
                          creator a Google trip and a copyright headache. */}
                      <details className="mt-3 text-[11px]" data-testid="promo-reel-free-music">
                        <summary className="cursor-pointer text-[color:var(--accent)] hover:opacity-80 inline-flex items-center gap-1.5">
                          <Music className="w-3 h-3" /> Need a track? Free, safe-to-use sources
                        </summary>
                        <ul className="mt-2 space-y-1.5 text-[color:var(--muted)] leading-relaxed">
                          <li>
                            <a
                              href="https://pixabay.com/music/"
                              target="_blank" rel="noopener noreferrer"
                              className="text-white hover:text-[color:var(--accent)] underline-offset-2 hover:underline"
                            >Pixabay Music</a>
                            {" — "}CC0, no attribution required, MP3 download. Best first stop for cinematic + emotional cues.
                          </li>
                          <li>
                            <a
                              href="https://mixkit.co/free-stock-music/"
                              target="_blank" rel="noopener noreferrer"
                              className="text-white hover:text-[color:var(--accent)] underline-offset-2 hover:underline"
                            >Mixkit Free Stock Music</a>
                            {" — "}free commercial licence, no sign-up. Great for short hype/upbeat reels.
                          </li>
                          <li>
                            <a
                              href="https://uppbeat.io/browse/music"
                              target="_blank" rel="noopener noreferrer"
                              className="text-white hover:text-[color:var(--accent)] underline-offset-2 hover:underline"
                            >Uppbeat</a>
                            {" — "}free with attribution credit (paid plan removes it). Strong instrumental library.
                          </li>
                          <li>
                            <a
                              href="https://freemusicarchive.org/genre/Soundtrack/"
                              target="_blank" rel="noopener noreferrer"
                              className="text-white hover:text-[color:var(--accent)] underline-offset-2 hover:underline"
                            >Free Music Archive · Soundtrack</a>
                            {" — "}filter by CC0 or CC-BY; deep classical/ambient catalogue (good for rescue / charity stories).
                          </li>
                          <li>
                            <a
                              href="https://incompetech.com/music/royalty-free/music.html"
                              target="_blank" rel="noopener noreferrer"
                              className="text-white hover:text-[color:var(--accent)] underline-offset-2 hover:underline"
                            >Incompetech</a>
                            {" — "}Kevin MacLeod's CC-BY 4.0 catalogue. Credit "Music by Kevin MacLeod" anywhere visible.
                          </li>
                        </ul>
                        <p className="mt-2 text-[10px] text-[color:var(--muted)] italic">
                          Quick tip: an instrumental 30–60 s track at 90–120 BPM tends to slot under a Storylo promo without competing with the on-screen text overlays. Look for "no vocals" / "cinematic" / "underscore" filters when you browse.
                        </p>
                      </details>
                    </div>
                  )}

                  {audioMode === "narration" && (
                    <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-2">
                      <textarea
                        value={narrationText}
                        onChange={(e) => setNarrationText(e.target.value.slice(0, 800))}
                        rows={3}
                        placeholder="Write a short intro narration that plays under your reel. e.g. ‘These are the stories that flow at Storylo — real animals, real rescues, real second chances.’"
                        className="input-base w-full text-sm p-2"
                        data-testid="promo-reel-narration-text"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={narrationLang}
                          onChange={(e) => setNarrationLang(e.target.value)}
                          className="input-base px-2 py-1.5 text-xs"
                          data-testid="promo-reel-narration-lang"
                        >
                          {PROMO_LANGS.map((l) => (
                            <option key={l.code} value={l.code}>{l.label}</option>
                          ))}
                        </select>
                        <select
                          value={narrationVoice}
                          onChange={(e) => setNarrationVoice(e.target.value)}
                          disabled={narrationVoices.length === 0}
                          className="input-base px-2 py-1.5 text-xs"
                          data-testid="promo-reel-narration-voice"
                        >
                          {narrationVoices.map((v) => (
                            <option key={v.id} value={v.id}>{v.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="text-[10px] text-[color:var(--muted)] text-right">
                        {narrationText.length}/800
                      </div>

                      {/* ── Optional quiet background music underneath the voice-over ── */}
                      <div className="mt-2 pt-3 border-t border-white/5">
                        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={bgMusicEnabled}
                            onChange={(e) => setBgMusicEnabled(e.target.checked)}
                            className="accent-[color:var(--accent)]"
                            data-testid="promo-reel-bg-music-toggle"
                          />
                          <Music className="w-3.5 h-3.5 text-[color:var(--accent)]" />
                          <span>Add quiet background music under the voice-over</span>
                        </label>

                        {bgMusicEnabled && (
                          <div className="mt-2 ml-6 space-y-2">
                            <label className="flex items-center gap-2 text-xs cursor-pointer hover:text-[color:var(--accent)]">
                              <Upload className="w-3.5 h-3.5" />
                              <span>{bgMusicFile ? `Replace: ${bgMusicFile.name}` : "Choose music file (mp3, wav, m4a, ≤ 20 MB)"}</span>
                              <input
                                type="file"
                                accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*"
                                className="hidden"
                                onChange={onBgMusicFileChosen}
                                data-testid="promo-reel-bg-music-file"
                              />
                            </label>
                            {bgMusicUploading && (
                              <div className="text-[10px] text-[color:var(--muted)] inline-flex items-center gap-1.5">
                                <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                              </div>
                            )}
                            {bgMusicUploaded && !bgMusicUploading && (
                              <div className="text-[10px] text-emerald-300 inline-flex items-center gap-1.5">
                                <Check className="w-3 h-3" /> {(bgMusicUploaded.size / 1024 / 1024).toFixed(2)} MB uploaded
                              </div>
                            )}
                            <label className="block">
                              <div className="flex items-baseline justify-between mb-1">
                                <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Music volume</span>
                                <span className="text-xs text-[color:var(--accent)]" data-testid="promo-reel-bg-music-volume-label">
                                  {Math.round(bgMusicVolume * 100)}%
                                </span>
                              </div>
                              <input
                                type="range" min={5} max={60} step={5}
                                value={Math.round(bgMusicVolume * 100)}
                                onChange={(e) => setBgMusicVolume(Number(e.target.value) / 100)}
                                className="w-full accent-[color:var(--accent)]"
                                data-testid="promo-reel-bg-music-volume"
                              />
                              <div className="flex justify-between text-[10px] text-[color:var(--muted)]">
                                <span>5%</span><span>25%</span><span>60%</span>
                              </div>
                            </label>
                            <p className="text-[10px] text-[color:var(--muted)] italic">
                              25% is the sweet spot — quiet enough that the narration leads, loud enough to set the mood. Push lower for solemn rescue stories, higher for upbeat reels.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>

                {/* ── Clip length slider ──────────────────────────── */}
                <section className="space-y-4 mb-5">
                  <label className="block">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Seconds per clip</span>
                      <span className="text-sm font-display text-[color:var(--accent)]" data-testid="promo-reel-clip-seconds">{clipSeconds}s</span>
                    </div>
                    <input
                      type="range" min={2} max={8} value={clipSeconds}
                      onChange={(e) => setClipSeconds(Number(e.target.value))}
                      disabled={running}
                      className="w-full accent-[color:var(--accent)]"
                      data-testid="promo-reel-clip-slider"
                    />
                    <div className="flex justify-between text-[10px] text-[color:var(--muted)] mt-0.5">
                      <span>2s</span><span>4s</span><span>8s</span>
                    </div>
                  </label>
                </section>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="panel p-3 border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Final length</div>
                    <div className="font-display text-2xl mt-1 text-[color:var(--accent)]">~{Math.round(totalLength)}s</div>
                    <div className="text-[10px] text-[color:var(--muted)] mt-0.5">incl. intro + outro</div>
                  </div>
                  <div className="panel p-3 border border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Build time</div>
                    <div className="font-display text-2xl mt-1">~{estimatedSeconds}s</div>
                    <div className="text-[10px] text-[color:var(--muted)] mt-0.5">we'll show progress</div>
                  </div>
                </div>
              </>
            )}

            {running && job && (
              <div className="space-y-3 mb-5" data-testid="promo-reel-progress">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-[color:var(--accent)]" />
                  <span>{job.message || "Working…"}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-[color:var(--accent)] transition-all duration-500"
                    style={{ width: `${job.progress || 0}%` }}
                  />
                </div>
                <div className="text-[10px] text-[color:var(--muted)] text-right">{job.progress || 0}%</div>
              </div>
            )}

            {job?.status === "failed" && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mb-3 inline-flex items-start gap-2" data-testid="promo-reel-failed">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{job.message || "Build failed."}</span>
              </div>
            )}

            {job?.status === "done" && job?.download_url && (
              <div className="mb-5" data-testid="promo-reel-result">
                <video
                  src={`${BACKEND_URL}${job.download_url}`}
                  controls
                  className="w-full rounded-lg bg-black aspect-video"
                  data-testid="promo-reel-video"
                />
                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-[color:var(--muted)]">
                  <span>{job.stories || 0} stories</span>
                  {job.file_bytes && <span>· {(job.file_bytes / 1024 / 1024).toFixed(1)} MB</span>}
                  {job.audio_kind && (
                    <span>· {job.audio_kind}{job.has_bg_music ? " + bg music" : ""}</span>
                  )}
                  {job.skipped && job.skipped.length > 0 && (
                    <span className="text-amber-300">· {job.skipped.length} skipped</span>
                  )}
                </div>
                {job.skipped && job.skipped.length > 0 && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-amber-300 hover:text-amber-200">Why some stories were skipped</summary>
                    <ul className="mt-2 space-y-1">
                      {job.skipped.map((s, i) => (
                        <li key={i} className="text-[color:var(--muted)]">
                          <strong className="text-white/90">{s.title}</strong> · {s.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {err && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mb-3" data-testid="promo-reel-error">{err}</div>
            )}

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-white/5">
              <button
                onClick={onClose}
                disabled={running}
                className="text-xs px-3 py-2 rounded-md border border-white/10 hover:border-white/30 transition-colors disabled:opacity-40"
                data-testid="promo-reel-cancel"
              >
                {job?.status === "done" ? "Close" : "Cancel"}
              </button>
              {job?.status === "done" && job?.download_url ? (
                <a
                  href={`${BACKEND_URL}${job.download_url}`}
                  download={`storylo-promo-${job.id?.slice(0, 8) || "reel"}.mp4`}
                  className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                  data-testid="promo-reel-download"
                >
                  <Download className="w-4 h-4" /> Download MP4
                </a>
              ) : (
                <button
                  onClick={onBuild}
                  disabled={running || audioUploading}
                  className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="promo-reel-build"
                >
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
                  {running ? "Building…" : "Build promo"}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
